import { Box, Button, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { onlineClassesPath } from "@outschool/routes";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";

function LanguageLevelCategory({
  level,
  description,
  buttonLabel,
  path,
  color,
}: {
  level: string;
  description: string;
  buttonLabel: string;
  path: string;
  color: string;
}) {
  const navigate = useNavigation();
  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        width: "250px",
      }}
    >
      <Box
        flex
        sx={{
          marginTop: "-30px",
          width: "100px",
          height: "100px",
          borderRadius: "50px",
          backgroundColor: color,
          alignItems: "center",
          justifyContent: "center",
          transform: "translateY(50px)",
          border: "1px solid #000",
        }}
      >
        <Typography variant="h5">{level}</Typography>
      </Box>
      <Box
        sx={{
          padding: "15px",
          paddingTop: "55px",
          borderRadius: "10px",
          boxShadow: "0 16px 32px 0 #0000001A, 0 0 4px 0 #0000000A",
          flexDirection: "column",
          alignItems: "center",
          gap: "1em",
        }}
        flex
      >
        <Box sx={{ height: "60px", paddingTop: "5px" }}>
          <Typography variant="h5">{description}</Typography>
        </Box>
        <Button
          to={path}
          variant="outlined"
          size="medium"
          onClick={() => navigate(path)}
          sx={{
            borderColor: color,
            color: "#000000",
          }}
        >
          {buttonLabel}
        </Button>
      </Box>
    </Box>
  );
}

export default function EnglishClassesByLevelCategories() {
  const { t } = useTranslation(
    "ui-components-website\\EnglishClassesByLevelCategories"
  );
  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "80px",
      }}
    >
      <Box
        flex
        sx={{
          padding: "1em",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: "10px",
          gap: "0.5em",
        }}
      >
        <Typography variant="h2">
          {t("English Classes for Every Level")}
        </Typography>
        <Box
          flex
          sx={{
            justifyContent: "center",
            gap: "1.5em",
            flexWrap: "wrap",
          }}
        >
          <LanguageLevelCategory
            level={t("A0")}
            description={t("For students who are new to English")}
            buttonLabel={t("Browse A0 Classes")}
            path={onlineClassesPath("cefr-pre-a1-absolute-beginner")}
            color={"grey.100"}
          />
          <LanguageLevelCategory
            level={t("A0.5")}
            description={t("Novice (<1 year) and below")}
            buttonLabel={t("Browse A0.5 Classes")}
            path={onlineClassesPath("cefr-pre-a1-novice")}
            color={"#AFE1AF"}
          />
          <LanguageLevelCategory
            level={t("Pre-A1")}
            description={t("Advanced Novice (1-2 years) and below")}
            buttonLabel={t("Browse Pre-A1 Classes")}
            path={onlineClassesPath("cefr-pre-a1-advanced-novice")}
            color={"#FFC0CB"}
          />
          <LanguageLevelCategory
            level={t("A1")}
            description={t("Beginner (2-3 years) and below")}
            buttonLabel={t("Browse A1 Classes")}
            path={onlineClassesPath("cefr-a1-beginner")}
            color={"#ADD8E6"}
          />
        </Box>
        <Box
          flex
          sx={{
            justifyContent: "center",
            gap: "2em",
            flexWrap: "wrap",
          }}
        >
          <LanguageLevelCategory
            level={t("A2")}
            description={t("Elementary and below")}
            buttonLabel={t("Browse A2 Classes")}
            path={onlineClassesPath("cefr-a2-elementary")}
            color="#FFDB58"
          />
          <LanguageLevelCategory
            level={t("B1")}
            description={t("Intermediate and below")}
            buttonLabel={t("Browse B1 Classes")}
            path={onlineClassesPath("cefr-b1-intermediate")}
            color="#F88379"
          />
          <LanguageLevelCategory
            level={t("B2")}
            description={t("Upper-Intermediate and below")}
            buttonLabel={t("Browse B2 Classes")}
            path={onlineClassesPath("cefr-b2-upper-intermediate")}
            color="#CBC3E3"
          />
        </Box>
      </Box>
    </Box>
  );
}
